import { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useLazyQuery } from "@apollo/client"
import { Box, Button, HStack, Spinner, Stack, Text, VStack, Center } from "@chakra-ui/react"
import { FaBuilding, FaCheck, FaXmark, FaUserPlus } from "react-icons/fa6"
import { acceptInvitation, rejectInvitation } from "../auth/auth"
import { ValidateInvitationDocument } from "@hydrologiq/gql-experience/graphql"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { useAuth } from "../auth/AuthProvider"
// Helper to capitalize first letter
const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
}
import LogoCard from "../components/layout/LogoCard"
import Link from "@hydrologiq/shared/components/Link"
export const AcceptInvitation = () => {
  const { invitationId } = useParams<{ invitationId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invitationInfo, setInvitationInfo] = useState<{
    valid: boolean
    message: string
    invitationId?: string
    organizationName?: string
    role?: string
    email?: string
  } | null>(null)

  const [validateInvitation] = useLazyQuery(ValidateInvitationDocument)

  // Load invitation details
  useEffect(() => {
    const loadInvitation = async () => {
      if (!invitationId) {
        setError("Invalid invitation link")
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // For unauthenticated users, we can get invitation details by ID only
        // For authenticated users, we validate with their email
        const result = await validateInvitation({
          variables: {
            email: user?.email || undefined, // Only validate email if user is authenticated
            invitationId: invitationId,
          },
        })

        if (result.data?.validateInvitation) {
          const validation = result.data.validateInvitation
          setInvitationInfo({
            valid: validation.valid,
            message: validation.message,
            invitationId: validation.invitationId || undefined,
            organizationName: validation.organizationName || undefined,
            role: validation.role || undefined,
            email: validation.email || user?.email, // Use invitation email or current user's email
          })
        } else {
          setError("Invitation not found or expired")
        }
      } catch (err) {
        console.error("Failed to load invitation:", err)
        setError("Invalid or expired invitation")
      } finally {
        setLoading(false)
      }
    }

    loadInvitation()
  }, [invitationId, validateInvitation, user?.email])

  const handleAccept = useCallback(async () => {
    if (!invitationId || !invitationInfo?.valid) return

    // If user is not authenticated, redirect to signup with invitation details
    if (!user) {
      const signupUrl = new URL("/auth/sign-up", window.location.origin)
      signupUrl.searchParams.set("invitation", invitationId)
      if (invitationInfo.email) {
        signupUrl.searchParams.set("email", invitationInfo.email)
      }
      window.location.href = signupUrl.toString()
      return
    }

    setAccepting(true)
    try {
      const result = await acceptInvitation({ invitationId })

      if (result?.data) {
        toaster.success({
          title: "Invitation accepted",
          description: `You've joined ${invitationInfo.organizationName}`,
        })

        // Redirect to home
        navigate("/")
      } else {
        throw new Error("Failed to accept invitation")
      }
    } catch (err: any) {
      console.error("Failed to accept invitation:", err)
      toaster.error({
        title: "Failed to accept invitation",
        description: err.message || "Please try again",
      })
    } finally {
      setAccepting(false)
    }
  }, [invitationId, invitationInfo, user, navigate])

  const handleReject = useCallback(async () => {
    if (!invitationId) return

    setRejecting(true)
    try {
      await rejectInvitation({ invitationId })

      toaster.success({
        title: "Invitation rejected",
      })

      // Redirect to home
      navigate("/")
    } catch (err: any) {
      console.error("Failed to reject invitation:", err)
      toaster.error({
        title: "Failed to reject invitation",
        description: err.message || "Please try again",
      })
    } finally {
      setRejecting(false)
    }
  }, [invitationId, navigate])

  if (loading) {
    return (
      <LogoCard>
        <Center>
          <VStack gap={4} bg="white">
            <Spinner size="xl" />
            <Text>Loading invitation...</Text>
          </VStack>
        </Center>
      </LogoCard>
    )
  }

  if (error) {
    return (
      <LogoCard>
        <VStack gap={4}>
          <Box color="red.500" fontSize="4xl">
            <FaXmark />
          </Box>
          <Text fontSize="xl" fontWeight="bold">
            Invalid Invitation
          </Text>
          <Text textAlign="center">{error}</Text>
          <Button onClick={() => navigate("/")} variant="hydrologiq_primary">
            Go to Home
          </Button>
        </VStack>
      </LogoCard>
    )
  }

  // Check if invitation is invalid
  if (!invitationInfo?.valid) {
    return (
      <LogoCard>
        <VStack gap={4}>
          <Box color="orange.500" fontSize="4xl">
            <FaXmark />
          </Box>
          <Text fontSize="xl" fontWeight="bold">
            Invalid Invitation
          </Text>
          <Text textAlign="center">{invitationInfo?.message || "This invitation is not valid or has expired."}</Text>
          <Button onClick={() => navigate("/")} variant="hydrologiq_primary">
            Go to Home
          </Button>
        </VStack>
      </LogoCard>
    )
  }

  return (
    <LogoCard>
      <VStack gap={6} align="stretch">
        <VStack gap={4}>
          <Box color="blue" fontSize="4xl">
            <FaBuilding />
          </Box>

          <HStack gap={1}>
            <Text>You've been invited to join</Text>
            <Text fontWeight="semibold">{invitationInfo.organizationName || "Organisation"}</Text>
            <Text>with the role</Text>
            <Text fontWeight="semibold">{capitalizeFirstLetter(invitationInfo.role || "member")}</Text>
          </HStack>
        </VStack>

        {/* Actions - Different for authenticated vs unauthenticated users */}
        <Stack gap={3} width="100%">
          {user ? (
            // Authenticated user - can accept/reject directly
            <>
              <Button
                variant="hydrologiq_primary"
                size="lg"
                onClick={handleAccept}
                loading={accepting}
                disabled={accepting || rejecting}
              >
                <FaCheck />
                Accept Invitation
              </Button>

              <Button
                variant="warning"
                size="lg"
                onClick={handleReject}
                loading={rejecting}
                disabled={accepting || rejecting}
              >
                <FaXmark />
                Reject
              </Button>
            </>
          ) : (
            // Unauthenticated user - needs to sign up
            <>
              <Button variant="hydrologiq_primary" size="lg" onClick={handleAccept} loading={accepting}>
                <FaUserPlus />
                Setup Account to Join
              </Button>
              <Center>
                <Link href="/" fontSize="sm" variant="primary" internal>
                  Already have an account? Sign in to join
                </Link>
              </Center>
            </>
          )}
        </Stack>
      </VStack>
    </LogoCard>
  )
}
